import GearIcon from "../../assets/icons/GearIcon";
import NotificationIcon from "../../assets/icons/NotifcationIcon";
import NotificationUnreadIcon from "../../assets/icons/NotifcationUnreadIcon";
import TutorialIcon from "../../assets/icons/TutorialIcon";
import useNotifications from "../../utils/hooks/notifications/useNotifications";
import useModal from "../../utils/hooks/useModal";
import useUserDetails from "../../utils/hooks/user/useUserDetails";
import { ModalType } from "../../utils/store/reducer/slices/modalReducer";
import NotificationsModal from "../notifications/NotificationsModal";
import ProfileImageSmall from "../profile/ProfileImageSmall";
import TutorialModal from "../modals/TutorialModal";
import FeedbackReminderModal from "../notifications/FeedbackReminderModal";
import React, { useEffect, useRef, useState } from "react";
import { useStoreSelector } from "../../utils/hooks/hooks";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { Link } from "react-router-dom";
import ProfileIcon from "../../assets/icons/ProfileIcon";
import HelpIcon from "../../assets/icons/HelpIcon";
import { getClientScopingStatus } from "../../utils/api/endpoints/questionnaire";
import { ClipboardList, ShieldAlert, Sparkles } from "lucide-react";


/**
 * The top header thats a part of the common dashbord layout.
 *
 * Includes a demonstration of Tailwind
 */
const DashboardHeader = ({ className }: { className?: string }) => {
  const { profilePicture, user_id, username, displayName } = useUserDetails();
  const { newNotifications, unreadCount } = useNotifications();
  const { modal, setModal, preventClose } = useModal(ModalType.NOTIFICATIONS);
  const tutorialModal = useModal(ModalType.TUTORIAL);
  const userRole = useStoreSelector(state => state.user.credentials.userRole);
  
  const [activeDraftToken, setActiveDraftToken] = useState<string | null>(null);
  const [activeDraftStatus, setActiveDraftStatus] = useState<string | null>(null);

  const getRoleName = (role: UserRole) => {
    switch (role) {
      case UserRole.RESEARCHER: return "Researcher";
      case UserRole.BUSINESS: return "Business Owner";
      case UserRole.ADMIN: return "Admin";
      case UserRole.QA: return "QA";
      case UserRole.ADMIN_MANAGER: return "Admin Manager";
      case UserRole.DEVELOPER: return "Security Engineer";
      case UserRole.BUSINESS_MANAGER: return "Program Manager";
      case UserRole.BUSINESS_ADMINISTRATOR: return "Organization Admin";
      case UserRole.SUB_ADMIN: return "Sub Admin";
      case UserRole.SOLUTIONS_SPECIALIST: return "Solutions Specialist";
      default: return "";
    }
  };

  const isBusinessRole = [
    UserRole.BUSINESS,
    UserRole.BUSINESS_MANAGER,
    UserRole.BUSINESS_ADMINISTRATOR
  ].includes((userRole as unknown) as UserRole);

  const isFeedbackAllowedRole = [
    UserRole.BUSINESS,
    UserRole.BUSINESS_ADMINISTRATOR
  ].includes((userRole as unknown) as UserRole);

  const [showTutorialTip, setShowTutorialTip] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Debug: Log user role and business role check
  useEffect(() => {
    console.log("DashboardHeader - User role:", userRole);
    console.log("DashboardHeader - Is business role:", isBusinessRole);
  }, [userRole, isBusinessRole]);

  // Popover visibility: show unless opted out, only for business roles
  useEffect(() => {
    const resolvedKey = `${user_id ?? username ?? "anon"}`;
    const optOutResolved = typeof window !== "undefined" ? localStorage.getItem(`ctb:tutorialTipOptOut:${resolvedKey}`) === "1" : false;
    const optOutAnon = typeof window !== "undefined" ? localStorage.getItem(`ctb:tutorialTipOptOut:anon`) === "1" : false;
    setShowTutorialTip(isBusinessRole && !(optOutResolved || optOutAnon));
  }, [user_id, username, isBusinessRole]);

  useEffect(() => {
    const fetchScopingStatus = async () => {
      if (isBusinessRole) {
        try {
          const res = await getClientScopingStatus();
          if (!res.locked && res.activeDraftToken) {
            setActiveDraftToken(res.activeDraftToken || null);
            setActiveDraftStatus(res.activeDraftStatus || null);
          } else {
            setActiveDraftToken(null);
            setActiveDraftStatus(null);
          }
        } catch (error) {
          console.error("Error fetching client scoping status in header:", error);
        }
      }
    };
    fetchScopingStatus();
  }, [isBusinessRole]);

  const setOptOut = () => {
    const resolvedKey = `${user_id ?? username ?? "anon"}`;
    try {
      localStorage.setItem(`ctb:tutorialTipOptOut:${resolvedKey}`, "1");
      localStorage.setItem(`ctb:tutorialTipOptOut:anon`, "1");
    } catch { }
    setShowTutorialTip(false);
  };

  // Close popover when clicking elsewhere
  const tutorialWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showTutorialTip) return;
    const handler = (e: MouseEvent) => {
      if (tutorialWrapRef.current && !tutorialWrapRef.current.contains(e.target as Node)) {
        if (dontShowAgain) {
          setOptOut();
        } else {
          setShowTutorialTip(false);
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showTutorialTip, dontShowAgain]);

  return (
    <header className={"flex items-center justify-between px-4 border-b border-gray-200 bg-white " + className}>

      {/* Left Aligned Pending Scope Banner */}
      <div className="flex items-center gap-3">
        {activeDraftToken && (
          <a
            href={`/intake/${activeDraftToken}`}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-bold transition shadow-sm hover:shadow active:scale-95 border ${
              activeDraftStatus === "SUBMITTED"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200/80 animate-pulse"
                : "bg-[#081549] text-white border-[#081549] hover:bg-[#081549]/90"
            }`}
          >
            {activeDraftStatus === "SUBMITTED" ? (
              <>
                <ShieldAlert size={13} className="text-emerald-600 shrink-0" />
                <span>Scope Under Review: Specialist is verifying your scope</span>
              </>
            ) : (
              <>
                <ClipboardList size={13} className="text-white/95 shrink-0" />
                <span className="flex items-center gap-1 font-black uppercase tracking-wider">
                  New Scope Pending
                  <Sparkles size={11} className="text-amber-400 animate-bounce shrink-0" />
                </span>
              </>
            )}
          </a>
        )}
      </div>

      <div className="flex items-center gap-4">
        {/* Tutorial button - shown only for business roles */}
        {/* {isBusinessRole && (
        <div className="relative" ref={tutorialWrapRef}>
          <button
            className="flex items-center justify-center rounded-full p-1.5 transition hover:bg-blue-50"
            title="Watch tutorial"
            onClick={() => { tutorialModal.setModal(true); tutorialModal.preventClose(); }}
          >
            <TutorialIcon className="stroke-blue-600" />
          </button>

          {showTutorialTip && (
            <div
              className="absolute right-0 top-8 z-50 w-[360px] select-none rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-base font-semibold text-gray-900">Ready to launch your pentest program?</div>
              <p className="mt-1 text-[13px] leading-5 text-gray-600">Hit play to see a quick walkthrough that shows you exactly how to set up your first test.</p>
              <div className="mt-4 flex items-center justify-between">
                <button
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
                  onClick={() => { tutorialModal.setModal(true); tutorialModal.preventClose(); }}
                >
                  Watch walkthrough
                </button>
                <div className="flex items-center gap-3">
                  <button
                    className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
                    onClick={() => { if (dontShowAgain) setOptOut(); else setShowTutorialTip(false); }}
                  >
                    Close
                  </button>
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600 select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={dontShowAgain}
                      onChange={e => setDontShowAgain(e.target.checked)}
                    />
                    Don't show this again
                  </label>
                </div>
              </div>
              <span className="pointer-events-none absolute -top-1 right-6 h-2 w-2 rotate-45 border-t border-l border-gray-200 bg-white"></span>
            </div>
          )}
        </div>
        )} */}

        {/* Notifications button */}
        <Link
          to="/dashboard/notifications"
          title="Notifications"
          className="relative flex items-center justify-center rounded-full p-1.5 transition hover:bg-gray-100"
        >
          <NotificationIcon className="stroke-[#6B7280] h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex min-h-[16px] min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 py-0.5 text-[9px] font-bold text-white shadow-sm ring-1 ring-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Link>

        <Link
          to="/dashboard/onboarding-documentation"
          title="Documentation"
          className="flex items-center justify-center rounded-full p-1.5 transition hover:bg-gray-100"
        >
          <HelpIcon className="stroke-[#6B7280] h-5 w-5" />
        </Link>

        {/* Settings button - opens settings page */}
        <a
          href={[
            UserRole.RESEARCHER
          ].includes((userRole as unknown) as UserRole)
            ? "/dashboard/profile/edit/settings"
            : "/dashboard/settings"}
          title="Settings"
          className="flex items-center justify-center rounded-full p-1.5 transition hover:bg-gray-100"
        >
          <GearIcon className="stroke-[#6B7280]" />
        </a>



        {/* User Info */}
        <div className="flex flex-col items-end mr-2">
          <span className="text-sm font-medium text-gray-900">
            {displayName || username || "User"}
          </span>
          <span className="text-xs text-gray-500">
            {getRoleName((userRole as unknown) as UserRole)}
          </span>
        </div>

        {/* Profile picture, defaults to blank grey circle if one cant be found */}
        <Link to="/dashboard/profile" className="flex items-center justify-center rounded-full transition hover:opacity-80">
          {profilePicture ? (
            <ProfileImageSmall
              src={profilePicture}
              className="border-2 border-slate-600 bg-ctb-grey-200"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-slate-600 bg-ctb-grey-200">
              <ProfileIcon className="h-6 w-6 stroke-slate-500" />
            </div>
          )}
        </Link>
      </div>

      {/* Tutorial modal */}
      {tutorialModal.modal && (
        <TutorialModal
          isOpen={tutorialModal.modal}
          onClose={() => tutorialModal.setModal(false)}
        />
      )}

      {/* Feedback Reminder Modal - Only for authorized business users */}
      {isFeedbackAllowedRole && (
        <>
          {console.log("DashboardHeader - Rendering FeedbackReminderModal")}
          <FeedbackReminderModal />
        </>
      )}
    </header>
  );
};

export default DashboardHeader;
